import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Icon } from "@iconify/react";

const ToastContext = createContext(null);

const ToastContainer = ({ toasts, onDismiss }) => {
  // State to track modal presence
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Detect modal presence with MutationObserver
  useEffect(() => {
    const checkModalPresence = () => {
      const modal = document.querySelector('[role="dialog"]');
      setIsModalOpen(!!modal);
    };

    // Initial check on mount
    checkModalPresence();

    // Setup observer to watch for modal changes
    const observer = new MutationObserver(checkModalPresence);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => observer.disconnect();
  }, []);

  // Separate toasts into centered and normal
  const centeredToasts = toasts.filter((t) => t.centered);
  const normalToasts = toasts.filter((t) => !t.centered);

  // Dynamic positioning based on modal state for normal toasts
  const normalContainerClasses = isModalOpen
    ? "fixed left-1/2 -translate-x-1/2 bottom-[20%] z-[1400] flex flex-col-reverse gap-2 max-w-md"
    : "fixed top-4 right-4 z-[1400] flex flex-col gap-2 max-w-md";

  // Centered toasts always appear in the center
  const centeredContainerClasses =
    "fixed left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 z-[1400] flex flex-col gap-2 max-w-md";

  return (
    <>
      {/* Normal toasts (errors, success) */}
      {normalToasts.length > 0 && (
        <div className={normalContainerClasses}>
          {normalToasts.map((toast) => (
            <div
              key={toast.id}
              className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
                toast.type === "error"
                  ? "bg-red-50 border border-red-200 text-red-800"
                  : toast.type === "success"
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-yellow-50 border border-yellow-200 text-yellow-800"
              }`}
            >
              {/* Icon */}
              {toast.type === "error" ? (
                <Icon
                  icon="tabler:alert-circle"
                  className="w-5 h-5 flex-shrink-0"
                />
              ) : toast.type === "success" ? (
                <Icon
                  icon="tabler:circle-check"
                  className="w-5 h-5 flex-shrink-0"
                />
              ) : (
                <Icon
                  icon="tabler:alert-triangle"
                  className="w-5 h-5 flex-shrink-0"
                />
              )}

              {/* Message */}
              <span className="text-sm font-medium flex-1">
                {toast.message}
              </span>

              {/* Close button */}
              <button
                onClick={() => onDismiss(toast.id)}
                className="flex-shrink-0 hover:opacity-70 transition"
                aria-label="Dismiss"
              >
                <Icon icon="tabler:xbox-x" className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Centered toasts (warnings, permission denials) */}
      {centeredToasts.length > 0 && (
        <div className={centeredContainerClasses}>
          {centeredToasts.map((toast) => (
            <div
              key={toast.id}
              className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${
                toast.type === "error"
                  ? "bg-red-50 border border-red-200 text-red-800"
                  : toast.type === "success"
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-yellow-50 border border-yellow-200 text-yellow-800"
              }`}
            >
              {/* Icon */}
              {toast.type === "error" ? (
                <Icon
                  icon="tabler:alert-circle"
                  className="w-5 h-5 flex-shrink-0"
                />
              ) : toast.type === "success" ? (
                <Icon
                  icon="tabler:circle-check"
                  className="w-5 h-5 flex-shrink-0"
                />
              ) : (
                <Icon
                  icon="tabler:alert-triangle"
                  className="w-5 h-5 flex-shrink-0"
                />
              )}

              {/* Message */}
              <span className="text-sm font-medium flex-1">
                {toast.message}
              </span>

              {/* Close button */}
              <button
                onClick={() => onDismiss(toast.id)}
                className="flex-shrink-0 hover:opacity-70 transition"
                aria-label="Dismiss"
              >
                <Icon icon="tabler:xbox-x" className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(
    (type, message, duration = 5000, centered = false) => {
      const id = Date.now() + Math.random(); // Ensure uniqueness
      setToasts((prev) => [...prev, { id, type, message, centered }]);
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    },
    []
  );

  const showError = useCallback(
    (message) => addToast("error", message, 5000, false),
    [addToast]
  );
  const showSuccess = useCallback(
    (message) => addToast("success", message, 3000, false),
    [addToast]
  );
  const showWarning = useCallback(
    (message) => addToast("warning", message, 5000, true),
    [addToast]
  );

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showError, showSuccess, showWarning }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
